import React, { useCallback, useEffect, useState } from 'react';
import { Flag } from 'lucide-react';
import { resolveUrl } from '../api';
import { RADIUS, TYPE, useTheme } from '../theme';
import { Badge, Button, Card, Dialog, EmptyState, ErrorState, Field, Loader, Segmented, Textarea } from '../components/ui';
import { DEFAULT_WARNING_TEXT } from '../components/moderation/ModerationNotice';
import { modAdminApi, TARGET_LABEL, type AdminAction, type AdminReport, type SuspendedUser } from '../components/moderation/moderationApi';

// AS1c · Admin → Moderering: rapporter med innehållet och åtgärderna avfärda, ta bort,
// varna och stänga av. Avstängda användare listas längst ner och kan släppas in igen.
// API:t kräver admin för varje anrop (routes/moderation.ts).

const fmt = (ms: number) => new Date(ms).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' });

export function ModerationQueue() {
  const T = useTheme();
  const [status, setStatus] = useState<'open' | 'closed'>('open');
  const [reports, setReports] = useState<AdminReport[] | null>(null);
  const [suspended, setSuspended] = useState<SuspendedUser[]>([]);
  const [error, setError] = useState('');
  const [acting, setActing] = useState<AdminReport | null>(null);

  const load = useCallback(() => {
    setError('');
    modAdminApi.reports(status).then(setReports).catch(e => { setError(e.message); setReports([]); });
    modAdminApi.suspended().then(setSuspended).catch(() => {});
  }, [status]);
  useEffect(() => { setReports(null); load(); }, [load]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <p style={{ ...TYPE.small, color: T.textSub, margin: 0, flex: '1 1 260px' }}>
          Anmält innehåll från chatt, album, annonser och profiler. Den som anmälde är anonym för den anmälda.
        </p>
        <Segmented size="sm" block={false} value={status} onChange={setStatus} label="Visa"
          options={[{ value: 'open', label: 'Att hantera' }, { value: 'closed', label: 'Hanterade' }]} />
      </div>
      {error && <ErrorState message={error} />}
      {reports === null ? <Loader text="Hämtar rapporter…" /> : reports.length === 0 && !error ? (
        <Card><EmptyState icon={Flag} title={status === 'open' ? 'Inga rapporter att hantera' : 'Inga hanterade rapporter än'} /></Card>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {reports.map(r => <ReportCard key={r.id} r={r} onAct={status === 'open' ? () => setActing(r) : undefined} />)}
        </div>
      )}

      {suspended.length > 0 && (
        <section style={{ marginTop: 28 }}>
          <h2 style={{ ...TYPE.label, color: T.textSub, margin: '0 0 10px' }}>Avstängda användare ({suspended.length})</h2>
          <div style={{ display: 'grid', gap: 8 }}>
            {suspended.map(u => (
              <Card key={u.email} padding={14} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                  <div style={{ color: T.text, fontWeight: 700, overflowWrap: 'anywhere' }}>{u.email}</div>
                  <div style={{ ...TYPE.small, color: T.textMuted }}>{fmt(u.suspendedAt)}{u.reason ? ` · ${u.reason}` : ''}{u.warnings ? ` · ${u.warnings} ${u.warnings === 1 ? 'varning' : 'varningar'}` : ''}</div>
                </div>
                <Button size="sm" variant="secondary" onClick={() => { if (confirm(`Släpp in ${u.email} igen?`)) modAdminApi.unsuspend(u.email).then(load).catch(e => alert(e.message)); }}>Häv avstängning</Button>
              </Card>
            ))}
          </div>
        </section>
      )}

      {acting && <ResolveDialog r={acting} onClose={() => setActing(null)} onDone={() => { setActing(null); load(); }} />}
    </div>
  );
}

function ReportCard({ r, onAct }: { r: AdminReport; onAct?: () => void }) {
  const T = useTheme();
  const s = r.snapshot;
  const img = s.imageUrl ?? (s.mediaType === 'image' ? s.url : null);
  return (
    <Card padding={16}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <Badge tone="danger">{r.reasonLabel}</Badge>
        <Badge>{TARGET_LABEL[r.targetType] ?? r.targetType}</Badge>
        {r.markName && <span style={{ ...TYPE.small, color: T.textMuted }}>{r.markName}</span>}
        <span style={{ ...TYPE.small, color: T.textMuted, marginLeft: 'auto' }}>{fmt(r.createdAt)}</span>
      </div>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {img && <a href={resolveUrl(img)} target="_blank" rel="noreferrer" style={{ flexShrink: 0 }}>
          <img src={resolveUrl(img)} alt="Anmäld bild" style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: RADIUS.md, display: 'block', background: T.surface2 }} />
        </a>}
        <div style={{ flex: '1 1 220px', minWidth: 0 }}>
          {(s.title || s.name) && <div style={{ color: T.text, fontWeight: 700, marginBottom: 4 }}>{s.title || s.name}</div>}
          {s.text ? <blockquote style={{ margin: 0, padding: '8px 12px', borderLeft: `3px solid ${T.border}`, background: T.surface2, borderRadius: RADIUS.sm, color: T.text, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 15 }}>{s.text}</blockquote>
            : !img && <div style={{ ...TYPE.small, color: T.textMuted }}>(ingen text)</div>}
          {s.mediaType === 'video' && s.url && <a href={s.url} target="_blank" rel="noreferrer" style={{ color: T.accentText, fontWeight: 600 }}>Öppna videon</a>}
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', margin: '12px 0 0', fontSize: 14 }}>
            <dt style={{ color: T.textSub }}>Användare</dt>
            <dd style={{ margin: 0, color: T.text, overflowWrap: 'anywhere' }}>
              {r.targetName || '–'}{r.targetEmail ? ` (${r.targetEmail})` : ''}
              {r.user && <span style={{ color: T.textMuted }}> · {r.user.reports} {r.user.reports === 1 ? 'anmälan' : 'anmälningar'}{r.user.warnings ? `, ${r.user.warnings} ${r.user.warnings === 1 ? 'varning' : 'varningar'}` : ''}{r.user.suspended ? ', avstängd' : ''}</span>}
            </dd>
            <dt style={{ color: T.textSub }}>Anmäld av</dt>
            <dd style={{ margin: 0, color: T.text, overflowWrap: 'anywhere' }}>{r.reporterName || r.reporterEmail} ({r.reporterEmail})</dd>
            {r.details && <><dt style={{ color: T.textSub }}>Kommentar</dt><dd style={{ margin: 0, color: T.text, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{r.details}</dd></>}
            {r.resolvedAt && <><dt style={{ color: T.textSub }}>Beslut</dt><dd style={{ margin: 0, color: T.text }}>{actionText(r.action)} · {r.resolvedBy} · {fmt(r.resolvedAt)}{r.adminNote ? ` – ${r.adminNote}` : ''}</dd></>}
          </dl>
        </div>
      </div>
      {onAct && <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}><Button size="sm" onClick={onAct}>Hantera…</Button></div>}
    </Card>
  );
}

const ACTION_TEXT: Record<AdminAction, string> = { dismiss: 'Avfärdad', remove: 'Borttaget', warn: 'Varnad', suspend: 'Avstängd' };
const actionText = (a: string | null) => (a ?? '').split(',').filter(Boolean).map(x => ACTION_TEXT[x as AdminAction] ?? x).join(', ') || '–';

function ResolveDialog({ r, onClose, onDone }: { r: AdminReport; onClose: () => void; onDone: () => void }) {
  const T = useTheme();
  const [remove, setRemove] = useState(true);
  const [warn, setWarn] = useState(false);
  const [suspend, setSuspend] = useState(false);
  const [warning, setWarning] = useState(DEFAULT_WARNING_TEXT);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const hasUser = !!r.targetEmail;
  const removeLabel = r.targetType === 'ad' ? 'Avpublicera annonsen' : r.targetType === 'profile' ? 'Ta bort profilbild och presentation' : 'Ta bort innehållet';

  async function run(actions: AdminAction[]) {
    if (!actions.length) { setError('Välj minst en åtgärd'); return; }
    setBusy(true); setError('');
    try {
      await modAdminApi.resolve(r.id, { actions, warning: warn ? warning : undefined, suspendReason: suspend ? r.reasonLabel : undefined, note });
      onDone();
    } catch (e: any) { setError(e.message); setBusy(false); }
  }
  const chosen = [remove && 'remove', warn && 'warn', suspend && 'suspend'].filter(Boolean) as AdminAction[];
  const check = (on: boolean, set: (v: boolean) => void, label: string, sub: string, disabled = false) => (
    <label style={{ display: 'flex', gap: 12, alignItems: 'flex-start', minHeight: 44, padding: '8px 0', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1 }}>
      <input type="checkbox" checked={on} disabled={disabled} onChange={e => set(e.target.checked)} style={{ width: 20, height: 20, accentColor: T.accentFill, marginTop: 2, flexShrink: 0 }} />
      <span><span style={{ display: 'block', fontWeight: 700, color: T.text }}>{label}</span><span style={{ display: 'block', ...TYPE.small, color: T.textMuted }}>{sub}</span></span>
    </label>
  );

  return (
    <Dialog open onClose={onClose} title="Hantera anmälan" width={520}
      actions={<>
        <Button variant="ghost" onClick={() => run(['dismiss'])} disabled={busy}>Avfärda</Button>
        <Button variant={suspend ? 'danger' : 'primary'} onClick={() => run(chosen)} loading={busy}>Genomför</Button>
      </>}>
      <p style={{ ...TYPE.small, color: T.textSub, margin: '0 0 8px' }}>
        {TARGET_LABEL[r.targetType]} av {r.targetName || r.targetEmail || 'okänd'} · {r.reasonLabel}
      </p>
      {check(remove, setRemove, removeLabel, 'Försvinner för alla. En kopia finns kvar i anmälan.')}
      {check(warn, setWarn, 'Varna användaren', 'Visas för användaren nästa gång hen öppnar appen.', !hasUser)}
      {warn && <Field label="Varningstext" style={{ margin: '0 0 8px 32px' }}><Textarea rows={3} value={warning} onChange={e => setWarning(e.target.value)} /></Field>}
      {check(suspend, setSuspend, 'Stäng av användaren', 'Kan inte logga in, skriva eller ladda upp förrän du häver avstängningen.', !hasUser)}
      <Field label="Anteckning (bara för admin)" style={{ marginTop: 8 }}>
        <Textarea rows={2} value={note} onChange={e => setNote(e.target.value)} />
      </Field>
      {error && <p role="alert" style={{ ...TYPE.small, color: T.danger, fontWeight: 600, margin: '10px 0 0' }}>{error}</p>}
    </Dialog>
  );
}
